import {
	type NavigateOptions,
	type RegisteredRouter,
	useRouter,
} from "@tanstack/react-router"

/**
 * Returns a back handler for a screen. With a router history entry behind the
 * current one it goes back. Without one it replaces the current entry with
 * `fallback`, the screen's parent. In practice that is a browser deep link (a
 * new tab, an external site): push and Telegram taps open on top of /home, so
 * they have an entry behind them.
 *
 * `to` must be an absolute path, so the fallback never depends on the current
 * location.
 */
export function useGoBack<TTo extends string>(
	fallback: NavigateOptions<RegisteredRouter, string, TTo> & {
		to: `/${string}`
	},
) {
	const router = useRouter()

	return () => {
		if (router.history.canGoBack()) {
			router.history.back()
			return
		}
		// The call site checks the fallback's route and params. A spread loses
		// that generic type, so the copy is widened to plain NavigateOptions.
		void router.navigate({ ...fallback, replace: true } as NavigateOptions)
	}
}
